import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeft, Save, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

import Overview from "@/components/tabs/Overview";
import Turns from "@/components/tabs/Turns";
import Characters from "@/components/tabs/Characters";
import Military from "@/components/tabs/Military";
import Settlements from "@/components/tabs/Settlements";
import MapTab from "@/components/tabs/MapTab";
import Nobility from "@/components/tabs/Nobility";
import ThievesGuild from "@/components/tabs/ThievesGuild";
import Dungeon from "@/components/tabs/Dungeon";
import Rules from "@/components/tabs/Rules";

const TAB_DEFS = [
  { key: "overview", label: "Overview", Comp: Overview },
  { key: "turns", label: "Turns", Comp: Turns },
  { key: "characters", label: "Characters", Comp: Characters },
  { key: "military", label: "Military", Comp: Military },
  { key: "settlements", label: "Settlements", Comp: Settlements },
  { key: "map", label: "Map", Comp: MapTab },
  { key: "nobility", label: "Nobility", Comp: Nobility },
  { key: "thieves", label: "Thieves Guild", Comp: ThievesGuild },
  { key: "dungeon", label: "Dungeon", Comp: Dungeon },
];

export default function Portal({ mode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const isAdmin = mode === "admin";
  const playerId = isAdmin ? params.playerId : user?.player_id;

  const [player, setPlayer] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (!playerId) return;
    api.get(`/players/${playerId}`).then(({ data }) => setPlayer(data));
    api.get(`/settings`).then(({ data }) => setSettings(data));
  }, [playerId]);

  const updatePlayer = useCallback((mutator) => {
    setPlayer((prev) => {
      const next = structuredClone(prev);
      mutator(next);
      return next;
    });
    setDirty(true);
  }, []);

  const savePlayer = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/players/${playerId}`, player);
      setPlayer(data);
      setDirty(false);
      toast.success("Changes saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put(`/settings`, patch);
      setSettings(data);
      toast.success("Saved for all kingdoms");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const saveCharacterBasics = async (patch) => {
    try {
      const { data } = await api.patch(`/players/${playerId}/character`, patch);
      setPlayer((prev) => ({ ...prev, character: data.character }));
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const addIncome = async () => {
    try {
      // persist current edits (esp. the monthly income amounts) first,
      // then credit them so the amounts are guaranteed to be up to date.
      await api.put(`/players/${playerId}`, player);
      const { data } = await api.post(`/players/${playerId}/add-income`);
      setPlayer((prev) => ({ ...prev, gold: data.gold, resources: data.resources }));
      setDirty(false);
      toast.success(`Monthly income applied (+${player.monthly_gold_income || 0}g, +${player.monthly_resource_income || 0}r)`);
    } catch (e) {
      toast.error("Could not apply income");
    }
  };

  if (!player || !settings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="ht-uppercase-label animate-pulse">Loading faction…</p>
      </div>
    );
  }

  const ActiveComp = active === "rules" ? Rules : TAB_DEFS.find((t) => t.key === active)?.Comp;
  const tabProps = {
    player, updatePlayer, editable: isAdmin, mode, playerId,
    settings, saveSettings, saveCharacterBasics, addIncome,
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-gold/25 sticky top-0 bg-background/85 backdrop-blur-md z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {isAdmin && (
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="rounded-sm shrink-0" data-testid="back-to-dashboard">
                <ArrowLeft strokeWidth={1.5} className="w-5 h-5" />
              </Button>
            )}
            <Logo className="h-8 w-auto object-contain shrink-0" />
            {isAdmin && (
              <span className="ht-uppercase-label truncate hidden sm:block">
                Editing · {player.kingdom_name || "Faction"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant={active === "rules" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActive(active === "rules" ? "overview" : "rules")}
              className="rounded-sm"
              data-testid="rules-button"
            >
              <ScrollText strokeWidth={1.5} className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Rules</span>
            </Button>
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={logout} className="rounded-sm" data-testid="logout-button">
              <LogOut strokeWidth={1.5} className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto" data-testid="tab-nav">
          {TAB_DEFS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`ht-tab -mb-px ${active === t.key ? "active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div key={active} className="ht-fade-up">
          {ActiveComp && <ActiveComp {...tabProps} />}
        </div>
      </main>

      {isAdmin && dirty && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-gold/40 bg-card/95 backdrop-blur z-30">
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <span className="ht-uppercase-label">Unsaved changes</span>
            <Button onClick={savePlayer} disabled={saving} className="rounded-sm font-heading" data-testid="save-changes-button">
              <Save strokeWidth={1.5} className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
